// Threads: TanStack Query hooks + type definitions.
//
// A thread is a rep's open line of CONVERSATION on a matter, not a work
// record: the work a rep owes the matter is its project's plan, so a thread
// carries no assignee and no schedule (TMF-319). The focused thread's
// (channel, state) pair drives the entire workspace layout
// (docs/alpha-design.md §5). Mirrors lib/cases.ts / lib/workflows.ts for
// their resources.
//
// The LIST carries rows only (TMF-360): the rail renders a slot from the row
// and reads the person off the cases and contacts collections beside it. The
// VIEW embeds the matter and its contact, which is how the workspace resolves
// which case it is on.

import { queryOptions, useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useNavigate } from "@tanstack/react-router";
import { useCallback, useEffect, useState } from "react";

import { halFetch, type HalCollection, type HalResource } from "~/lib/hal";
import type { Case } from "~/lib/cases";
import type { Contact } from "~/lib/contacts";

export type ThreadChannel = "call" | "text" | "form";
/** The telephony vendor an ingested call arrived through. Closed, mirroring
 *  enums.CallProvider — the API is the only thing that ever sets it. */
export type CallProvider = "Twilio" | "CallRail";
export type ThreadState = "active" | "waiting" | "scheduled" | "idle";

/** One open line of CONVERSATION on a matter. */
export type Thread = HalResource<{
  id: string;
  case_id: string;
  /** The plan task this conversation was opened against, when it has one. */
  task_id: string | null;
  /** The vendor an ingested call arrived through and that vendor's own id for
   *  it — together the ingest's idempotency key. Both null on a thread a rep
   *  opened by hand. */
  call_provider: CallProvider | null;
  external_call_id: string | null;
  channel: ThreadChannel;
  state: ThreadState;
  subject: string;
  /** A short-TTL presigned GET for the call's audio, minted per-read and never
   *  stored — the same mechanism behind a document's `preview_url` and a texted
   *  photograph's. Absent for a text, an unrecorded call, and a call whose
   *  audio has not landed yet. */
  recording_url?: string | null;
  last_activity_at: string | null;
  created_at: string;
  updated_at: string;
  _embedded?: {
    /** The matter this conversation is on, with the person it talks to.
     *  Present on the VIEW route only. */
    case?: Case;
    /** The person the conversation is with, whose texting consent a text on it
     *  asks (TMF-520). It can differ from the matter's current contact.
     *  Present on the VIEW route only. */
    contact?: Contact;
  };
}>;

// ── The rail's working set ─────────────────────────────────────────────

/**
 * How many threads the rail holds. The slots are keyboard-addressed
 * (Ctrl/Alt+1..5), so the count is the keyboard's, not the layout's.
 */
export const RAIL_SLOTS = 5;

/**
 * Rail order, most-wanted first. NEEDS ATTENTION beats everything: a live
 * call outranks a callback the firm owes (waiting = the ball is in OUR
 * court — enums.ThreadState), which outranks work booked for later, which
 * outranks a thread nobody is touching.
 *
 * A Record over the closed ThreadState union rather than a list, so adding a
 * state to the vocabulary fails to compile here until someone decides where
 * it belongs — which is the decision that would otherwise be made by accident,
 * silently, at the bottom of the rail.
 *
 * `scheduled` sits where it does because it is COMMITTED future work: more
 * than idle, less than a rep already holding the ball.
 */
const RAIL_PRIORITY: Record<ThreadState, number> = {
  active: 0,
  waiting: 1,
  scheduled: 2,
  idle: 3,
};

/**
 * Newest first, id as the tie-break. ONE declaration, because two questions
 * ask it: which of a matter's threads is its freshest, and how two threads of
 * the same rank order against each other. Ties break on id because two threads
 * created in the same clock tick would otherwise swap places between renders,
 * which is the one kind of movement nobody asked for.
 */
function byRecency(a: Thread, b: Thread): number {
  // Newest first, so the string compare runs b-against-a.
  const byCreated = b.created_at.localeCompare(a.created_at);
  if (byCreated !== 0) return byCreated;
  return a.id.localeCompare(b.id);
}

/**
 * One slot per MATTER: its freshest thread, and only that one. A rep who
 * called the same person three times owes them one line in the rail, not
 * three, and the freshest is the one the conversation is actually on.
 *
 * Keyed on case_id, which is required and immutable at the repo: a thread
 * cannot exist without its matter, so it is the whole key.
 */
function freshestPerCase(threads: Thread[]): Thread[] {
  const byCase = new Map<string, Thread>();
  for (const thread of threads) {
    const held = byCase.get(thread.case_id);
    if (held === undefined || byRecency(thread, held) < 0) byCase.set(thread.case_id, thread);
  }
  return [...byCase.values()];
}

/**
 * The rail's working set: what a rep most likely wants next, five slots deep
 * (TMF-339).
 *
 * Three rules, composed, in this order:
 *
 *   1. ONE SLOT PER MATTER, each matter's freshest thread standing for it.
 *   2. FINISHED MATTERS SINK: a thread whose matter reached a dead end ranks
 *      below every thread whose matter is still live, whatever state it is in.
 *      A settled matter's leftover conversation is not what a rep wants next,
 *      and it was holding a slot ahead of live work.
 *   3. RECENCY, not stability: needs-attention states first, newest first
 *      within each. A live call is the most important thing on the screen the
 *      moment it arrives, so it enters at the top and the slots below it
 *      shift. Ctrl/Alt+1..5 address whatever is listed NOW, which is the same
 *      promise the rail makes visually.
 *
 * So the sort key is (settled, state, created_at DESC, id).
 *
 * WHICH matters are settled is the caller's to say, because the thread list
 * carries no embeds (TMF-360): the layout reads the dead-end stages off the
 * cases collection it already holds and hands the ids down. A matter missing
 * from that set sorts as live, which is what an unknown matter is.
 */
export function railThreads(threads: Thread[], settledCaseIds: ReadonlySet<string>): Thread[] {
  return freshestPerCase(threads)
    .sort((a, b) => {
      const bySettled =
        Number(settledCaseIds.has(a.case_id)) - Number(settledCaseIds.has(b.case_id));
      if (bySettled !== 0) return bySettled;
      const byState = RAIL_PRIORITY[a.state] - RAIL_PRIORITY[b.state];
      if (byState !== 0) return byState;
      return byRecency(a, b);
    })
    .slice(0, RAIL_SLOTS);
}

/**
 * Every thread of ONE matter, newest first (TMF-405).
 *
 * The COUNTERPART of railThreads, and deliberately its inverse: the rail
 * collapses a matter to its freshest thread because a rep working their queue
 * owes each person one line, while a rep already inside a matter's workspace
 * needs the ones that collapse hid: the call they took an hour ago, the text
 * conversation the call is about.
 *
 * That inversion is the whole reason this exists. A new thread on the matter
 * being viewed EVICTS the open one from the rail (it is newer, so it wins the
 * matter's slot), which left the open conversation reachable only by going back
 * to Tower — and Tower opens the freshest thread, which is the one that just
 * evicted it. This is the list that never loses it.
 *
 * Recency, not state priority: this is history to move around in, not a queue
 * to be triaged, so "what happened most recently" is the only order that reads
 * as a conversation list.
 *
 * LAST ACTIVITY, not creation — which is where this parts company with the
 * rail's byRecency, deliberately. A text thread is created once and then lives
 * for the whole relationship, so ordering a matter's conversations by creation
 * buries the one they are actually texting on under calls that came after it.
 * It is also the field the strip DISPLAYS, and a list sorted by one timestamp
 * while showing another reads as broken: the rehearsal's own matter rendered
 * 5:07, 5:14, 4:20 down the row. This is the same order the API's own List
 * default uses (`last_activity_at DESC`).
 *
 * Pure — the route memoizes it and the tests read it directly.
 */
export function caseThreads(threads: Thread[], caseId: string): Thread[] {
  return threads
    .filter((t) => t.case_id === caseId)
    .sort((a, b) => {
      const byActivity = activityAt(b).localeCompare(activityAt(a));
      if (byActivity !== 0) return byActivity;
      return a.id.localeCompare(b.id);
    });
}

/** When a thread last did anything — the timestamp the strip both sorts and
 *  shows. Falls back to creation for a thread nothing has happened on yet. */
function activityAt(thread: Thread): string {
  return thread.last_activity_at ?? thread.created_at;
}

/** What a conversation's state dot says: its channel and its state. */
export function threadStateLabel(thread: Thread): string {
  return `${thread.channel} · ${thread.state}`;
}

export type ThreadCollection = HalCollection<Thread>;

export const THREADS_KEY = ["threads"] as const;

/** The board renders every open matter and thread at once (and its KPI tiles
 *  count them), so both collections are fetched in one page. `size` is
 *  goat's page-size param. */
const BOARD_PAGE_SIZE = 200;

/** No poll: `threads:all` on the same SSE signals channel
 *  keeps the rail — and, by prefix, the focused workspace — current. */
export const threadsListQueryOptions = queryOptions<ThreadCollection>({
  queryKey: THREADS_KEY,
  queryFn: ({ signal }) =>
    halFetch<ThreadCollection>(`/api/threads?size=${BOARD_PAGE_SIZE}`, { signal }),
});

export const threadQueryOptions = (id: string) =>
  queryOptions<Thread>({
    queryKey: [...THREADS_KEY, id] as const,
    queryFn: ({ signal }) => halFetch<Thread>(`/api/threads/${id}`, { signal }),
  });

/**
 * How often, and for how long after the accept, a browser that answered or
 * placed a call looks for the thread that call opened. The ingest opens it from
 * the vendor's own webhook, which can land after the browser connects; a thread
 * that has not appeared by the end of the window is left to the rail.
 */
const CALL_THREAD_POLL_MS = 1000;
export const CALL_THREAD_WINDOW_MS = 15_000;

/** The thread a call opened, by the call's key. */
const callThreadQueryOptions = (provider: CallProvider, callSid: string) =>
  queryOptions<ThreadCollection>({
    queryKey: ["call-thread", provider, callSid] as const,
    queryFn: ({ signal }) =>
      halFetch<ThreadCollection>(
        `/api/threads?call_provider=${encodeURIComponent(provider)}&external_call_id=${encodeURIComponent(callSid)}`,
        { signal },
      ),
  });

/** One accepted call's landing: where the rep was and when, and whether it has
 *  closed. */
interface Landing {
  callSid: string;
  href: string;
  acceptedAt: number;
  closed: boolean;
}

/**
 * Lands the rep on the thread the call they answered or placed opened, by
 * handing its id to `land`. A call lands at most once, only within
 * CALL_THREAD_WINDOW_MS of wall clock from the accept, and only while `href` is
 * still where the rep was at the accept: a navigation of their own since then
 * outranks it. A landing that has closed for any of those reasons disables the
 * lookup, so no invalidation or reconnect can fetch it again.
 */
export function useCallLanding(
  provider: CallProvider,
  callSid: string | null,
  href: string,
  land: (threadId: string) => void,
): void {
  const [landing, setLanding] = useState<Landing | null>(null);
  if (callSid !== null && landing?.callSid !== callSid) {
    setLanding({ callSid, href, acceptedAt: Date.now(), closed: false });
  }
  const current = landing !== null && landing.callSid === callSid ? landing : null;
  const open = current !== null && !current.closed && current.href === href;

  const { data } = useQuery({
    ...callThreadQueryOptions(provider, callSid ?? ""),
    enabled: open,
    refetchInterval: open ? CALL_THREAD_POLL_MS : false,
  });
  const threadId = data?._embedded?.[0]?.id ?? null;

  const close = useCallback((sid: string) => {
    setLanding((prior) => (prior !== null && prior.callSid === sid ? { ...prior, closed: true } : prior));
  }, []);

  useEffect(() => {
    if (current === null || current.closed || current.href === href) return;
    close(current.callSid);
  }, [current, href, close]);

  useEffect(() => {
    if (!open) return;
    const timer = setTimeout(
      () => close(current.callSid),
      current.acceptedAt + CALL_THREAD_WINDOW_MS - Date.now(),
    );
    return () => clearTimeout(timer);
  }, [open, current, close]);

  useEffect(() => {
    if (!open || threadId === null) return;
    close(current.callSid);
    if (Date.now() - current.acceptedAt < CALL_THREAD_WINDOW_MS) land(threadId);
  }, [open, current, threadId, close, land]);
}

/** Fields a thread POST accepts. State defaults to idle server-side. */
export interface ThreadCreate {
  case_id: string;
  channel: ThreadChannel;
  state?: ThreadState;
  subject?: string;
}

export function useCreateThreadMutation() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (body: ThreadCreate) =>
      halFetch<Thread>("/api/threads", { method: "POST", body }),
    // No cache priming with the POST body: the create response carries no
    // embeds, and the workspace route needs _embedded.case, so its loader
    // fetches the full payload.
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: THREADS_KEY });
    },
  });
}

/**
 * Lands the rep on a matter, the way the Tower's cards and a new lead both do:
 * its freshest conversation, or a new call conversation when it has none, the
 * same POST the API's other clients use, then that conversation's workspace.
 * `opening` is true while the new conversation is being created.
 */
export function useOpenMatter(): { open: (matter: Case) => void; opening: boolean } {
  const { data } = useQuery(threadsListQueryOptions);
  const navigate = useNavigate();
  const createThread = useCreateThreadMutation();

  const land = (threadId: string) => void navigate({ to: "/alpha/threads/$threadId", params: { threadId } });
  const open = (matter: Case) => {
    const existing = (data?._embedded ?? [])
      .filter((t) => t.case_id === matter.id)
      .sort((a, b) => (b.last_activity_at ?? "").localeCompare(a.last_activity_at ?? ""))[0];
    if (existing) {
      land(existing.id);
      return;
    }
    createThread.mutate({ case_id: matter.id, channel: "call" }, { onSuccess: (t) => land(t.id) });
  };

  return { open, opening: createThread.isPending };
}
